import { Link } from 'react-router-dom';
import {
  LEP_EQ_ID,
  COMMAND_CENTER_METRICS,
  TEST_MANAGEMENT_FEATURES,
  AUTOMATED_TEST_TYPES,
  AI_VALIDATION_CHECKS,
  PERFORMANCE_METRICS,
  LOAD_TEST_SCENARIOS,
  SECURITY_VALIDATION_CHECKS,
  CHAOS_SCENARIOS,
  CERTIFICATION_TYPES,
  DISASTER_RECOVERY_TESTS,
  TEST_DATA_TYPES,
  BUG_INTELLIGENCE_FEATURES,
  CONTINUOUS_QUALITY_SIGNALS,
  ACCESSIBILITY_STANDARDS,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_SCORES,
  DEMO_TEST_SUITES,
  DEMO_AUTOMATED_TESTS,
  DEMO_AI_VALIDATION,
  DEMO_PERFORMANCE,
  DEMO_SECURITY,
  DEMO_CERTIFICATIONS,
  DEMO_CONTINUOUS_QUALITY,
  buildQualityDashboard,
} from '../../enterprisequality/framework';
import {
  QualityCommandCenterPanel,
  TestManagementPanel,
  AutomatedTestingPanel,
  AIValidationPanel,
  PerformanceEngineeringPanel,
  SecurityValidationPanel,
  ReleaseCertificationPanel,
  ContinuousQualityPanel,
} from '../../components/enterprisequality';
import { Card, KpiCard } from '../../components/ui';

export function EnterpriseQualityPage() {
  const dashboard = buildQualityDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{LEP_EQ_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Enterprise Quality Platform</h1>
        <p className="mt-1 text-sm text-slate-500">
          Enterprise Quality Assurance, Security Validation, Performance Engineering & Platform Certification —
          the release quality gate of the AI Commerce Operating System.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Command Center Metrics" value={dashboard.command_center_metrics.toString()} />
        <KpiCard label="Test Management" value={dashboard.test_management_features.toString()} />
        <KpiCard label="Automated Test Types" value={dashboard.automated_test_types.toString()} />
        <KpiCard label="Certification Types" value={dashboard.certification_types.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Architecture Pipeline</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Quality Command Center</h2>
        <p className="mt-1 text-sm text-slate-500">{COMMAND_CENTER_METRICS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <QualityCommandCenterPanel scores={DEMO_SCORES} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Test Management</h2>
          <p className="mt-1 text-sm text-slate-500">{TEST_MANAGEMENT_FEATURES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <TestManagementPanel suites={DEMO_TEST_SUITES} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Automated Testing</h2>
          <p className="mt-1 text-sm text-slate-500">{AUTOMATED_TEST_TYPES.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <AutomatedTestingPanel tests={DEMO_AUTOMATED_TESTS} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">AI Validation</h2>
        <p className="mt-1 text-sm text-slate-500">{AI_VALIDATION_CHECKS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <AIValidationPanel results={DEMO_AI_VALIDATION} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Performance Engineering</h2>
          <p className="mt-1 text-sm text-slate-500">{PERFORMANCE_METRICS.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <PerformanceEngineeringPanel benchmarks={DEMO_PERFORMANCE} />
          </Card>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Security Validation</h2>
          <p className="mt-1 text-sm text-slate-500">{SECURITY_VALIDATION_CHECKS.join(' · ')}</p>
          <Card className="mt-4" padding="sm">
            <SecurityValidationPanel checks={DEMO_SECURITY} />
          </Card>
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Release Certification</h2>
        <p className="mt-1 text-sm text-slate-500">{CERTIFICATION_TYPES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ReleaseCertificationPanel certifications={DEMO_CERTIFICATIONS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Continuous Quality</h2>
        <p className="mt-1 text-sm text-slate-500">{CONTINUOUS_QUALITY_SIGNALS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ContinuousQualityPanel signals={DEMO_CONTINUOUS_QUALITY} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Load & Scalability</h2>
          <p className="mt-2 text-xs text-slate-500">{LOAD_TEST_SCENARIOS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Chaos Engineering</h2>
          <p className="mt-2 text-xs text-slate-500">{CHAOS_SCENARIOS.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Disaster Recovery</h2>
          <p className="mt-2 text-xs text-slate-500">{DISASTER_RECOVERY_TESTS.join(' · ')}</p>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div>
          <h2 className="text-lg font-semibold">Test Data</h2>
          <p className="mt-2 text-xs text-slate-500">{TEST_DATA_TYPES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Bug Intelligence</h2>
          <p className="mt-2 text-xs text-slate-500">{BUG_INTELLIGENCE_FEATURES.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Accessibility</h2>
          <div className="mt-2 flex flex-wrap gap-1">
            {ACCESSIBILITY_STANDARDS.map((s) => (
              <span key={s} className="rounded border border-slate-200 px-2 py-0.5 text-xs dark:border-slate-700">{s}</span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
